'use client'

import { useState } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHeart as faHeartOutline } from '@awesome.me/kit-94e593bf23/icons/sharp/light'
import Tooltip from '@/app/_components/Tooltip'

import { useAppContext } from '@/app/_lib/_context'
import { useGlobalMessage, useFavorites } from '@/app/_lib/hooks'
import addFavoriteAction from '@/app/_lib/actions/AddFavoriteAction'

const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : ''

const ProductFavoriteMarker = ({ sub_product_id, isVisible, className, label, labelPosition = 'after', productName, size = 'text-xl' }) => {

    const { openFavoritesModal } = useAppContext()
    const { isFavorite: checkIsFavorite, refresh: refreshFavorites } = useFavorites()
    const addGlobalMessage = useGlobalMessage()

    const isFavorite = checkIsFavorite(parseInt(sub_product_id))
    const [isAdding, setIsAdding] = useState(false)
    const [justAdded, setJustAdded] = useState(false)
    const [isHovered, setIsHovered] = useState(false)

    const handleClick = async (event) => {
        event.preventDefault()
        event.stopPropagation()

        // If already a favorite, always open modal to allow remove/move
        if (isFavorite) {
            openFavoritesModal({
                hide_manage_button: false,
                sub_product_id: sub_product_id
            })
            return
        }

        // Not a favorite - check if we can auto-add (single list scenario)
        setIsAdding(true)
        try {
            const res = await fetch('/api/favorites/lists')
            const listsResponse = await res.json()

            if (listsResponse.success && listsResponse.favorites_lists) {
                const lists = listsResponse.favorites_lists

                // If only one list, auto-add without showing modal
                if (lists.length === 1) {
                    const requestData = {
                        sub_product_id: sub_product_id,
                        list_index: 0,
                    }
                    const addResponse = await addFavoriteAction(requestData)

                    if (addResponse.success) {
                        refreshFavorites()
                        setJustAdded(true)
                        setTimeout(() => setJustAdded(false), 800)
                        const displayName = productName ? capitalize(productName) : 'Item'
                        addGlobalMessage({
                            message: `${displayName} was added to ${lists[0].list_name}`,
                            messageType: 'success'
                        })
                    }
                    setIsAdding(false)
                    return
                }
            }

            // Multiple lists or couldn't fetch - fall back to modal
            setIsAdding(false)
            openFavoritesModal({
                hide_manage_button: false,
                sub_product_id: sub_product_id
            })
        } catch (error) {
            setIsAdding(false)
            // Fall back to modal on error
            openFavoritesModal({
                hide_manage_button: false,
                sub_product_id: sub_product_id
            })
        }
    }
    
    return (
        <Tooltip
            content={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            side="bottom"
            mobileMode="disabled"
            onOpenChange={setIsHovered}
        >
            <button
                type="button"
                onClick={handleClick}
                disabled={isAdding}
                aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
                aria-pressed={isFavorite}
                className={`${className} text-left outline-none group/favorites-button ${(!isFavorite && !isVisible) ? 'visible [@media(hover:hover)]:lg:invisible [@media(hover:hover)]:lg:group-hover:visible' : ''}`}
            >
                {label && labelPosition === 'before' && <span className="pr-1.5">{label}</span>}
                {isAdding ? (
                    <FontAwesomeIcon icon={faHeartOutline} aria-hidden="true" className={`${label ? '' : size} cursor-wait text-red animate-pulse`} />
                ) : (isFavorite || isHovered) ? (
                    <FontAwesomeIcon icon={faHeartOutline} aria-hidden="true" className={`${label ? '' : size} cursor-pointer text-red transition-transform duration-200 ${isHovered ? 'scale-110' : ''} ${justAdded ? 'animate-heart-wiggle' : ''}`} />
                ) : (
                    <FontAwesomeIcon icon={faHeartOutline} aria-hidden="true" className={`${label ? '' : size} cursor-pointer text-light transition-transform duration-200 hover:scale-110`} />
                )}
                {label && labelPosition === 'after' && <span className="pl-1.5">{label}</span>}
            </button>
        </Tooltip>
    )
}

export default ProductFavoriteMarker
